import { CdkAccordion, CdkAccordionItem } from '@angular/cdk/accordion';
import { CdkDragPlaceholder } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  inject,
  Inject,
  Injector,
  linkedSignal,
  OnDestroy,
  OnInit,
  Optional,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import {
  menuBar,
  MenuBarButton,
  MenuBarPillSwitch,
  MenuBarToggleButton,
  PageComponent,
  PageHeaderComponent,
} from '@softapps/core';
import {
  Application,
  ApplicationStore,
  LinkModule,
  MenuItemService,
  Refreshable,
  RefreshService,
  RouteMenuItem,
  SettingsService,
  SOFTLINE_FEATURE_APPLICATIONS,
  title,
} from '@softline/application';
import { AuthorizationStore, SOFTLINE_FEATURE_AUTHORIZATION } from '@softline/auth';
import { isDefinedNotEmpty, Store } from '@softline/core';
import {
  SOFTLINE_FEATURE_TRANSLATION,
  TranslationStore,
  translator,
} from '@softline/ui-core';
import { MenuBar } from '../../../../../core/src/lib/components/menu-bar/menu-bar/menu-bar';
import { Observable } from 'rxjs';
import {
  SOFTLINE_CONFIG_MODULES,
  SOFTLINE_CONFIG_MODULES_STATIC_LINKS,
} from '../module.providers';
import { ModuleSettings } from '../module.settings';
import { PinnedModulesService } from '../services/pinned-modules.service';
import {
  SOFTLINE_CONFIG_APP_OVERVIEW,
  SOFTLINE_SETTINGS_APP_OVERVIEW,
} from '../module.shared';
import { AppGridComponent } from './app-grid/app-grid.component';
import { AppListComponent } from './app-list/app-list.component';

import { IconComponent } from '@softline/ui-core';
import { InputComponent } from '@softline/ui-core';
import { TranslatePipe } from '@softline/ui-core';
@Component({
  selector: 'soft-app-overview',
  templateUrl: './app-overview.component.html',
  styleUrls: ['./app-overview.component.css'],
  imports: [
    CommonModule,
    AppGridComponent,
    AppListComponent,
    PageComponent,
    PageHeaderComponent,
    CdkAccordion,
    CdkAccordionItem,
    MenuBar,
    MenuBarButton,
    MenuBarPillSwitch,
    MenuBarToggleButton,
    CdkDragPlaceholder,
    IconComponent,
    InputComponent,
    TranslatePipe,
  ],
})
export class AppOverviewComponent implements OnInit, OnDestroy, Refreshable {
  readonly injector = inject(Injector);
  readonly pinnedService = inject(PinnedModulesService);
  private readonly menuItemService = inject(MenuItemService);
  private readonly settingsService = inject(SettingsService);

  private readonly staticNavRoutes = computed(() => {
    return new Set(
      this.menuItemService
        .menuItems()
        .filter((item) => item.type === 'route' && item.outlet === 'nav-static')
        .map((item) => (item as RouteMenuItem).routerLink?.[0])
    );
  });

  public readonly config = inject(SOFTLINE_CONFIG_APP_OVERVIEW, {
    optional: true,
  });

  private readonly modulesConfig = inject(SOFTLINE_CONFIG_MODULES, {
    optional: true,
  });

  isGrouped = signal(
    this.config?.isGroupButtonActive && this.config?.defaultView === 'grouped'
      ? true
      : false
  );

  newSearch = signal<string | undefined | null>('');

  nonPermittedAppsPermission = computed(() => {
    const isActive =
      this.config?.isNonPermittedAppsButtonActive ||
      this.permissions()?.includes('SOFTAPPS.MODULE.VIEW_NON_PERMITTED_APPS');
    return isActive;
  });

  disableNoPermissionApps = computed(() => {
    return this.config?.disableNoPermissionApps;
  });

  modulesSortedByAlphabet = computed(() => {
    return isDefinedNotEmpty(this.config?.isModulesSortedByAlphabet)
      ? this.config?.isModulesSortedByAlphabet
      : true;
  });

  showNonPermittedApps = signal(false);

  settings = this.settingsService.get<ModuleSettings>(SOFTLINE_SETTINGS_APP_OVERVIEW);

  private getTranslatedName(name: string): string {
    return this.store.get(
      SOFTLINE_FEATURE_TRANSLATION,
      TranslationStore.getters.translation,
      name
    );
  }

  search = '';

  applications$: Observable<readonly Application[]> = this.store.observe(
    SOFTLINE_FEATURE_APPLICATIONS,
    ApplicationStore.getters.items
  );

  readonly linkModules = signal<LinkModule[]>([]);
  readonly appModules = toSignal(this.applications$, {
    initialValue: [] as Application[],
  });

  readonly applications = computed(() => {
    const showNonPermitted = this.showNonPermittedApps();
    const disableNoPermissionApps = this.disableNoPermissionApps();
    const userPerms = (this.permissions() ?? []).map((p) => p.trim());

    const seen = new Set<string>();
    const uniqueModules = [...this.appModules(), ...this.linkModules()].filter((app) => {
      if (seen.has(app.name)) return false;
      seen.add(app.name);
      return true;
    });
    const apps = uniqueModules.map((app) => {
      const perm = (app.permission ?? '').trim();
      if (perm.length === 0) {
        return {
          ...app,
          disabled: disableNoPermissionApps ? true : false,
        } as Application & { disabled?: boolean };
      }
      const hasPermission = userPerms.includes(perm);
      return {
        ...app,
        disabled: !hasPermission,
      } as Application & { disabled?: boolean };
    });

    const visible = showNonPermitted ? apps : apps.filter((a) => !a.disabled);

    let sorted = visible;

    if (this.modulesSortedByAlphabet() && !this.modulesConfig?.preserveProviderOrder) {
      sorted = visible.sort((a, b) =>
        this.getTranslatedName(a.name).localeCompare(
          this.getTranslatedName(b.name),
          undefined,
          { sensitivity: 'base' }
        )
      );
    }

    const search = this.newSearch();
    if (!search || search.length === 0) {
      return sorted;
    }
    const searchTerm = search.toLowerCase();
    return sorted.filter((app) => {
      const nameMatches = this.getTranslatedName(app.name)
        .toLowerCase()
        .includes(searchTerm);
      const descriptionMatches =
        app.description?.toLowerCase().includes(searchTerm) ?? false;
      const groupMatches = app.group?.name?.toLowerCase().includes(searchTerm) ?? false;
      return nameMatches || descriptionMatches || groupMatches;
    });
  });

  readonly moduleGroups = linkedSignal(() => {
    const groups: {
      id: number;
      name: string;
      icon?: string;
      applications: Application[];
      expansion: boolean;
    }[] = [{ id: 0, name: 'none', applications: [], expansion: true }];

    for (const app of this.applications()) {
      const groupName = app.group?.name || 'none';
      const groupId = app.group?.id || 0;

      let group = groups.find((g) => g.name === groupName);
      if (!group) {
        group = {
          id: groupId,
          name: groupName,
          applications: [],
          expansion: true,
          icon: app.group?.icon,
        };
        groups.push(group);
      }

      group.applications.push(app);
    }

    const noneIdx = groups.findIndex((g) => g.name === 'none');
    if (noneIdx > -1 && groups[noneIdx].applications.length === 0) {
      groups.splice(noneIdx, 1);
    }

    return groups.sort((a, b) => {
      if (a.name === 'none') return -1;
      if (b.name === 'none') return 1;
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    });
  });

  setExpansion(groupId: number, expansion: boolean): void {
    const groups = this.moduleGroups();
    const group = groups.find((g) => g.id === groupId);
    if (group) {
      group.expansion = expansion;
    }
    this.moduleGroups.set(groups);
  }

  permissions$: Observable<readonly string[]> = this.store.observe(
    SOFTLINE_FEATURE_AUTHORIZATION,
    AuthorizationStore.getters.permissions
  );

  permissions = toSignal(this.permissions$);
  t = translator();

  readonly adminMenu = viewChild.required('adminMenuTemplate', {
    read: TemplateRef,
  });

  readonly overviewMenu = viewChild.required('overviewMenuTemplate', {
    read: TemplateRef,
  });

  constructor(
    public store: Store,
    protected router: Router,
    private refreshService: RefreshService,
    @Optional()
    @Inject(SOFTLINE_CONFIG_MODULES_STATIC_LINKS)
    _staticLinks?: LinkModule[]
  ) {
    this.linkModules.set(_staticLinks ?? []);
    title('Alle Module');
    menuBar({
      key: 'softapps/modules/app-overview',
      title: this.t('Start'),
      template: this.overviewMenu,
      containerStyle: 'plain',
      separated: true,
    });
    menuBar({
      key: 'softapps/modules/app-overview',
      title: this.t('Start'),
      template: this.adminMenu,
      containerStyle: 'plain',
      separated: true,
      align: 'trailing',
    });
  }

  async ngOnInit(): Promise<void> {
    this.refreshService.add(this);

    await this.store.dispatch(
      SOFTLINE_FEATURE_AUTHORIZATION,
      AuthorizationStore.actions.loadOnce
    );
  }

  ngOnDestroy(): void {
    this.refreshService.remove(this);
  }

  async refresh(): Promise<void> {
    await this.store.dispatch(
      SOFTLINE_FEATURE_AUTHORIZATION,
      AuthorizationStore.actions.load
    );
  }

  filterPermissions(
    application: Application,
    permissions: readonly string[] | undefined
  ): boolean {
    return (
      !application.permission || (permissions ?? []).indexOf(application.permission) > -1
    );
  }

  filterInput(
    application: Application,
    param?: { store: Store; input: string }
  ): boolean {
    if (!param) return false;
    const feature = SOFTLINE_FEATURE_TRANSLATION;
    const selector = TranslationStore.getters.translation;
    return param.store
      .get(feature, selector, application.name)
      .toLowerCase()
      .startsWith(param.input.toLowerCase());
  }

  async navigate(application: Application): Promise<void> {
    if (application?.type === 'link') {
      if (application?.['execute']) {
        await application.execute(this.injector);
      } else {
        window.open(application.href, '_blank');
      }
    } else {
      await this.router.navigate([application.route], application.routeParams);
    }
  }

  canPin(app: Application): boolean {
    if (app.type === 'link') return false;
    return !this.staticNavRoutes().has(app.route);
  }

  isPinned(app: Application): boolean {
    if (app.type === 'link') return false;
    return this.pinnedService.isPinned(app.route);
  }

  togglePin(app: Application): void {
    if (app.type === 'link') return;
    this.pinnedService.toggle(app.route);
  }

  mode = computed(() => this.settings()?.display ?? 'grid');

  setMode(mode: 'grid' | 'list'): void {
    this.settingsService.setKey(SOFTLINE_SETTINGS_APP_OVERVIEW, { display: mode });
    this.settingsService.save();
  }
}
